import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const REAL_WORK = '/portfolio/p1_ritual.webp';

export const Philosophy = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', 'end start'],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ['-10%', '20%']);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);
    const copyY = useTransform(scrollYProgress, [0, 1], ['6%', '-6%']);

    return (
        <section
            ref={ref}
            data-testid="philosophy-section"
            className="relative min-h-[90vh] md:min-h-screen w-full overflow-hidden flex items-center film-grain"
        >
            {/* Parallax background */}
            <motion.div
                style={{ y: bgY, scale: bgScale }}
                className="absolute inset-0 z-0 will-change-transform"
            >
                <img
                    src={REAL_WORK}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                />
            </motion.div>

            {/* Overlays */}
            <div className="absolute inset-0 z-10 bg-ink/55" />
            <div className="absolute inset-0 z-10 bg-gradient-to-b from-ink/40 via-transparent to-ink/70" />

            {/* Content */}
            <motion.div
                style={{ y: copyY }}
                className="relative z-20 w-full px-6 md:px-12 lg:px-20 py-24"
            >
                <div className="max-w-5xl">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-120px' }}
                        transition={{ duration: 0.9 }}
                        className="text-[11px] uppercase tracking-[0.45em] text-gold mb-8"
                    >
                        Our Philosophy · Why Bros On Board
                    </motion.p>

                    <motion.h2
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-120px' }}
                        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                        className="font-serif font-light text-bone tracking-tighter leading-[1.02] text-4xl sm:text-5xl md:text-6xl lg:text-[5.5rem]"
                    >
                        We shoot the{' '}
                        <span className="italic text-gold">connection</span>
                        <br />
                        between a bride and groom —
                        <br />
                        <span className="text-bone-dim italic">
                            you feel it inside the frame.
                        </span>
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.9, delay: 0.3 }}
                        className="mt-10 max-w-2xl text-bone-dim text-base md:text-lg font-light leading-relaxed"
                    >
                        A wedding carries too many emotions for a camera to simply{' '}
                        <em>record</em>. We direct the room, read the glance, and let the
                        audience feel what the couple felt — long after the lights go down.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.6 }}
                        className="mt-14 flex items-center gap-5"
                    >
                        <span className="w-14 h-px bg-gold" />
                        <div>
                            <p className="font-serif text-bone text-xl italic">— Balu Cine</p>
                            <p className="text-[10px] uppercase tracking-[0.35em] text-bone-dim mt-1">
                                Director · Bros On Board
                            </p>
                        </div>
                    </motion.div>
                </div>
            </motion.div>
        </section>
    );
};
